import { SearchInputType } from "./search-input-type.enum";

/**
 * Utility functions for SearchInputType enum
 * Centralizes UI-related logic for input types
 */
export class SearchInputTypeUtils {
  /**
   * Get Material Design icon for search input type
   * @param type Search input type
   * @returns Material icon name
   */
  static getIcon(type: SearchInputType): string {
    switch (type) {
      case SearchInputType.COORDINATE:
        return "my_location";
      case SearchInputType.WKT_GEOMETRY:
        return "gesture";
      case SearchInputType.GOOGLE_PLACE:
        return "place";
      case SearchInputType.TEXT:
        return "search";
      default:
        return "search";
    }
  }

  /**
   * Get display label for search input type
   * @param type Search input type
   * @returns Human-readable label
   */
  static getLabel(type: SearchInputType): string {
    switch (type) {
      case SearchInputType.COORDINATE:
        return "Location";
      case SearchInputType.WKT_GEOMETRY:
        return "Geometry";
      case SearchInputType.GOOGLE_PLACE:
        return "Address";
      case SearchInputType.TEXT:
        return "Text Search";
      default:
        return "Search";
    }
  }

  /**
   * Get CSS class for visual styling based on type
   * @param type Search input type
   * @returns CSS class name
   */
  static getColorClass(type: SearchInputType): string {
    switch (type) {
      case SearchInputType.COORDINATE:
        return "suggestion-coordinate";
      case SearchInputType.WKT_GEOMETRY:
        return "suggestion-geometry";
      case SearchInputType.GOOGLE_PLACE:
        return "suggestion-address";
      case SearchInputType.TEXT:
        return "suggestion-text";
      default:
        return "suggestion-text";
    }
  }

  /**
   * Get color code for type-specific UI elements
   * @param type Search input type
   * @returns Hex color code
   */
  static getColor(type: SearchInputType): string {
    switch (type) {
      case SearchInputType.COORDINATE:
        return "#4CAF50"; // Green
      case SearchInputType.WKT_GEOMETRY:
        return "#2196F3"; // Blue
      case SearchInputType.GOOGLE_PLACE:
        return "#FF9800"; // Orange
      case SearchInputType.TEXT:
        return "#9E9E9E"; // Grey
      default:
        return "#9E9E9E";
    }
  }

  /**
   * Check if input type should be saved to history
   * @param type Search input type
   * @returns True if should be saved to history
   */
  static shouldSaveToHistory(type: SearchInputType): boolean {
    return type === SearchInputType.GOOGLE_PLACE || type === SearchInputType.TEXT;
  }

  /**
   * Check if input type is generated in real-time (not from history)
   * @param type Search input type
   * @returns True if generated real-time
   */
  static isRealTimeGenerated(type: SearchInputType): boolean {
    return type === SearchInputType.COORDINATE || type === SearchInputType.WKT_GEOMETRY;
  }

  /**
   * Check if input type triggers immediate map action
   * @param type Search input type
   * @returns True if triggers immediate action
   */
  static triggersImmediateAction(type: SearchInputType): boolean {
    return (
      type === SearchInputType.COORDINATE ||
      type === SearchInputType.WKT_GEOMETRY ||
      type === SearchInputType.GOOGLE_PLACE
    );
  }

  /**
   * Check if input type requires server search
   * @param type Search input type
   * @returns True if requires server search
   */
  static requiresServerSearch(type: SearchInputType): boolean {
    return type === SearchInputType.TEXT;
  }
}
